<template>
  <div
    class="coach-mark"
    :class="[`is-${direction}`, `align-${align}`, { 'flip-sm': flipSm }]"
    :style="{
      '--offset-x': `${offset.x ?? 0}px`,
      '--offset-y': `${offset.y ?? 0}px`,
      '--tail-x': `${16 + (tailOffset.x ?? 0)}px`,
    }"
  >
    <span class="txt">
      <slot>{{ text }}</slot>
    </span>
    <span
      v-if="closable"
      class="coach-close"
      role="button"
      aria-label="닫기"
      @click.stop.prevent="$emit('close')"
    >×</span>
  </div>
</template>

<script lang="ts" setup>
withDefaults(defineProps<{
  /** 코치마크에 표시할 문구 (slot 으로 대체 가능) */
  text?: string
  /** 대상 기준 위치 (below: 대상 아래 / above: 대상 위) */
  direction?: 'below' | 'above'
  /** 가로 정렬 (left: 대상 왼쪽 기준 / center: 가운데 / right: 대상 오른쪽 기준) */
  align?: 'left' | 'center' | 'right'
  /** 모바일에서 above 를 아래로 뒤집기 */
  flipSm?: boolean
  /** 닫기(×) 버튼 표시 여부 */
  closable?: boolean
  /** 말풍선 위치 오프셋 (px) */
  offset?: { x?: number; y?: number }
  /** 꼬리 위치 오프셋 (px, 기본 16px 기준) */
  tailOffset?: { x?: number }
}>(), {
  text: '',
  direction: 'below',
  align: 'left',
  flipSm: false,
  closable: true,
  offset: () => ({}),
  tailOffset: () => ({}),
})

defineEmits<{
  (e: 'close'): void
}>()
</script>

<style scoped>
.coach-mark {
  position: absolute;
  z-index: 60;
  display: flex;
  align-items: center;
  gap: 8px;
  width: max-content;
  max-width: min(270px, calc(100vw - 32px));
  padding: 8px 14px;
  border-radius: var(--br-md, 10px);
  background-color: var(--color-primary, #ff5b5b);
  box-shadow: 0 6px 16px rgba(0, 0, 0, .18);
  white-space: normal;
  word-break: keep-all;
  animation: coach-mark-pop .5s cubic-bezier(.34, 1.56, .64, 1) .2s both;
}

.coach-mark .txt {
  font-size: 13px;
  font-weight: 400;
  color: #fff;
  line-height: 1.3;
  letter-spacing: 0;
  text-align: left
}

.coach-mark .coach-close {
  flex-shrink: 0;
  color: #fff;
  font-size: 15px;
  line-height: 1;
  cursor: pointer
}

/* 꼬리 공통 */
.coach-mark::after {
  content: '';
  position: absolute;
  border: 6px solid transparent
}

/* 방향: 대상 아래 (꼬리 위로) */
.coach-mark.is-below {
  top: calc(100% + 10px);
}

.coach-mark.is-below::after {
  bottom: 100%;
  border-bottom-color: var(--color-primary, #ff5b5b)
}

/* 방향: 대상 위 (꼬리 아래로) */
.coach-mark.is-above {
  bottom: calc(100% + 8px)
}

.coach-mark.is-above::after {
  top: 100%;
  border-top-color: var(--color-primary, #ff5b5b)
}

/* 가로 정렬 + 꼬리 위치 */
.coach-mark.align-left {
  left: 0;
  right: auto;
  translate: var(--offset-x, 0px) var(--offset-y, 0px)
}

.coach-mark.align-left::after {
  left: var(--tail-x, 16px);
  right: auto
}

.coach-mark.align-center {
  left: 50%;
  right: auto;
  translate: calc(-50% + var(--offset-x, 0px)) var(--offset-y, 0px)
}

.coach-mark.align-center::after {
  left: calc(50% + var(--tail-x, 0px) - 6px);
  right: auto
}

.coach-mark.align-right {
  right: 0;
  left: auto;
  translate: var(--offset-x, 0px) var(--offset-y, 0px)
}

.coach-mark.align-right::after {
  right: var(--tail-x, 16px);
  left: auto
}

/* 팝 애니메이션 기준점 (방향·정렬 조합) */
.coach-mark.is-below.align-left {
  transform-origin: 16px 0
}

.coach-mark.is-below.align-center {
  transform-origin: 50% 0
}

.coach-mark.is-below.align-right {
  transform-origin: calc(100% - 16px) 0
}

.coach-mark.is-above.align-left {
  transform-origin: 16px 100%
}

.coach-mark.is-above.align-center {
  transform-origin: 50% 100%
}

.coach-mark.is-above.align-right {
  transform-origin: calc(100% - 16px) 100%
}

/* 모바일: is-above 를 아래로 뒤집기 */
@media(max-width:767px) {
  .coach-mark.flip-sm.is-above {
    bottom: auto;
    top: calc(100% + 10px)
  }

  .coach-mark.flip-sm.is-above::after {
    top: auto;
    bottom: 100%;
    border-top-color: transparent;
    border-bottom-color: var(--color-primary, #ff5b5b)
  }

  .coach-mark.flip-sm.is-above.align-left {
    transform-origin: 16px 0
  }

  .coach-mark.flip-sm.is-above.align-right {
    transform-origin: calc(100% - 16px) 0
  }
}

@keyframes coach-mark-pop {
  0% {
    opacity: 0;
    transform: scale(.3)
  }

  100% {
    opacity: 1;
    transform: scale(1)
  }
}

@media (max-width: 300px) {
  .coach-mark {
    max-width: min(190px, calc(100vw - 32px))
  }
}

@media (prefers-reduced-motion: reduce) {
  .coach-mark {
    animation: none
  }
}
</style>
